import React, { FC, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Box, Button, IconButton, ListItem, Paper } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { pl } from "date-fns/locale";

import imageSliderController from "../../../../../Api/imageSliderController";
import { useToast } from "../../../../../hooks/useToast";
import CustomInput from "../../../../_common/CustomInput";
import { useConfirmDialog } from "../../../../../hooks/useConfirmDialog";
import { baseURL } from "../../../../../config";


const BASE_URL = baseURL;


const DateBox = styled(Box)`
    width: 100%;    
`

const DateDiv = styled.div`
    display: flex;
    justify-content: center;
`

const DeleteButton = styled(Button)`
    margin-top: 10px;
    width: 100%;
`

interface SliderItemProps {
    _id: string;
    image: string;
    link: string;
    name?: string;
    publish: Date | null;
    expire: Date | null;
    remove: Date | null;
    onRemoveSlide: (_id: string) => void;
}

const SliderItem: FC<SliderItemProps> = ({
    _id,
    image,
    link,
    name,
    publish,
    expire,
    remove,
    onRemoveSlide
}) => {
    const [imageLink, setImageLink] = useState(link || '');
    const [isLinkChanged, setIsLinkChanged] = useState(false);
    const [publishDate, setPublishDate] = useState<Date | null>(publish || null);
    const [isPublishDateOpen, setIsPublishDateOpen] = useState(false);
    const [expireDate, setExpireDate] = useState<Date | null>(expire || null);
    const [isExpireDateOpen, setIsExpireDateOpen] = useState(false);
    const [removeDate, setRemoveDate] = useState<Date | null>(remove || null);
    const [isRemoveDateOpen, setIsRemoveDateOpen] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const toast = useRef(useToast()).current
    const { confirm, dialog } = useConfirmDialog()
    const now = new Date();
    const isVisible =
        (!publishDate || new Date(publishDate) <= now) &&
        (!expireDate || new Date(expireDate) > now);


    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: _id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const handleChangePublishDate = async (date: Date | null) => {
        const data = {
            _id,
            publish: date ? date.toISOString() : null,
        }
        const res = await imageSliderController.editImageDates(data)
        if (!res.ok) return toast.error(res.message);
        setPublishDate(date);
        setIsPublishDateOpen(false);
    }

    const handleChangeExpireDate = async (date: Date | null) => {
        if (expireDate !== null) {
            const data = {
                _id,
                expire: date ? date.toISOString() : null,
            }
            const res = await imageSliderController.editImageDates(data);
            if (!res.ok) return toast.error(res.message);
            setExpireDate(date);
            return setIsExpireDateOpen(false);
        }
        const data = {
            _id,
            expire: date ? date.toISOString() : null,
            remove: date ? date.toISOString() : null,
        }
        const res = await imageSliderController.editImageDates(data);
        if (!res.ok) return toast.error(res.message);
        setExpireDate(date);
        setRemoveDate(date);
        setIsExpireDateOpen(false);
        setIsRemoveDateOpen(false);
    }

    const handleChangeRemoveDate = async (date: Date | null) => {
        const data = {
            _id,
            remove: date ? date.toISOString() : null,
        }
        const res = await imageSliderController.editImageDates(data);
        if (!res.ok) return toast.error(res.message);
        setRemoveDate(date);
        setIsRemoveDateOpen(false);
    }


    const handleDeleteSlide = async () => {
        const agreement = await confirm('Czy na pewno chcesz usunąć ten slajd?');
        if (!agreement) return;
        const res = await imageSliderController.deleteImage(_id);
        if (!res.ok) return toast.error(res.message);
        onRemoveSlide(_id);
        toast.success('Slajd został usunięty');
    }


    const handleDeleteDate = async (dateType: 'publish' | 'expire' | 'delete') => {
        if (dateType === 'publish') {
            const data = {
                _id,
                publish: null,
            }

            const res = await imageSliderController.editImageDates(data);
            if (!res.ok) return toast.error(res.message);
            setPublishDate(null);
            return setIsPublishDateOpen(false);
        }
        if (dateType === 'expire') {
            const data = {
                _id,
                expire: null,
            }
            const res = await imageSliderController.editImageDates(data);
            if (!res.ok) return toast.error(res.message);
            setExpireDate(null);
            return setIsExpireDateOpen(false);
        }
        if (dateType === 'delete') {
            const data = {
                _id,
                remove: null,
            }
            const res = await imageSliderController.editImageDates(data);
            if (!res.ok) return toast.error(res.message);
            setRemoveDate(null);
            return setIsRemoveDateOpen(false);
        }
    }


    useEffect(() => {
        if (imageLink === link) {
            setIsLinkChanged(false);
            return;
        }
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            (async () => {
                const res = await imageSliderController.setImageLink(_id, imageLink);
                if (res.ok) {
                    setIsLinkChanged(true);
                    setTimeout(() => {
                        setIsLinkChanged(false);
                    }, 2000);
                }
            })();
        }, 2000);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [imageLink, link, _id]);


    return (
        <ListItem
            key={_id}
            sx={{
                p: { xs: 2, md: 2.5 },
                border: '1px solid',
                borderColor: isVisible ? 'success.light' : 'warning.light',
                borderRadius: 3,
                alignItems: 'flex-start',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 2,
                bgcolor: '#fff',
                boxShadow: '0 7px 20px rgba(24, 35, 73, .07)'
            }}
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
        >
            {dialog}
            <IconButton
                size="small"
                sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    bgcolor: "background.paper",
                    ":hover": { bgcolor: "grey.200" }
                }}
                onClick={handleDeleteSlide}
                onPointerDown={e => e.stopPropagation()}
            >
                <Delete />
            </IconButton>
            <img style={{ width: 260, maxWidth: '100%', height: 170, objectFit: 'contain', borderRadius: 10 }} src={BASE_URL + image} alt={name || "slide"} />
            <Box sx={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', pr: { xs: 0, md: 3 } }}>
                <Box sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1,
                }}>
                    <>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                                borderRadius: 2,
                                mb: 2,
                                width: '100%',
                                height: '100%',
                                m: 0,
                                boxShadow: 'none'
                            }}
                        >
                            <Button
                                fullWidth
                                variant="outlined"
                                sx={{ mb: 2 }}
                                onClick={() => {
                                    setIsPublishDateOpen(!isPublishDateOpen);
                                }}
                                tabIndex={-1}
                                onPointerDown={e => e.stopPropagation()}
                            >
                                {publishDate !== null
                                    ? `Data publikacji: ${format(publishDate, "dd MMMM yyyy", { locale: pl })}`
                                    : "Wybierz datę publikacji"}
                            </Button>
                            {isPublishDateOpen && (
                                <DateBox>
                                    <DateDiv tabIndex={-1} onPointerDown={e => e.stopPropagation()}>
                                        <DatePicker
                                            selected={publishDate}
                                            onChange={((date: any) => handleChangePublishDate(date))}
                                            locale={pl}
                                            dateFormat="dd.MM.yyyy"
                                            inline
                                        />
                                    </DateDiv>
                                    {publishDate &&
                                        <DeleteButton
                                            color="error"
                                            variant="contained"
                                            onClick={() => handleDeleteDate('publish')}
                                            onPointerDown={e => e.stopPropagation()}
                                        >
                                            Usuń datę
                                        </DeleteButton>}
                                </DateBox>
                            )}
                        </Paper>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                                borderRadius: 2,
                                mb: 2,
                                width: '100%',
                                height: '100%',
                                m: 0,
                                boxShadow: 'none'
                            }}
                        >
                            <Button
                                color="secondary"
                                fullWidth
                                variant="outlined"
                                sx={{ mb: 2 }}
                                onClick={() => {
                                    setIsExpireDateOpen(!isExpireDateOpen);
                                }}
                                tabIndex={-1}
                                onPointerDown={e => e.stopPropagation()}
                            >
                                {expireDate !== null
                                    ? `Data wygaśnięcia: ${format(expireDate, "dd MMMM yyyy", { locale: pl })}`
                                    : "Wybierz datę wygaśnięcia"}
                            </Button>
                            {isExpireDateOpen && (
                                <DateBox>
                                    <DateDiv tabIndex={-1} onPointerDown={e => e.stopPropagation()}>
                                        <DatePicker
                                            selected={expireDate}
                                            onChange={(date: any) => handleChangeExpireDate(date)}
                                            locale={pl}
                                            dateFormat="dd.MM.yyyy"
                                            inline
                                        />
                                    </DateDiv>
                                    {expireDate && <DeleteButton
                                        color="error"
                                        variant="contained"
                                        onClick={() => handleDeleteDate('expire')}
                                        onPointerDown={e => e.stopPropagation()}
                                    >
                                        Usuń datę
                                    </DeleteButton>}
                                </DateBox>
                            )}
                        </Paper>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                                borderRadius: 2,
                                mb: 2,
                                width: '100%',
                                height: '100%',
                                m: 0,
                                boxShadow: 'none'
                            }}
                        >
                            <Button
                                color="error"
                                fullWidth
                                variant="outlined"
                                sx={{ mb: 2 }}
                                onClick={() => {
                                    setIsRemoveDateOpen(!isRemoveDateOpen);
                                }}
                                tabIndex={-1}
                                onPointerDown={e => e.stopPropagation()}
                            >
                                {removeDate !== null
                                    ? `Data usunięcia: ${format(removeDate, "dd MMMM yyyy", { locale: pl })}`
                                    : "Wybierz datę usunięcia"}
                            </Button>
                            {isRemoveDateOpen && (
                                <DateBox>
                                    <DateDiv tabIndex={-1} onPointerDown={e => e.stopPropagation()}>
                                        <DatePicker
                                            selected={removeDate}
                                            onChange={(date: any) => handleChangeRemoveDate(date)}
                                            locale={pl}
                                            dateFormat="dd.MM.yyyy"
                                            inline
                                        />
                                    </DateDiv>
                                    {removeDate && <DeleteButton
                                        color="error"
                                        variant="contained"
                                        onClick={() => handleDeleteDate('delete')}
                                        onPointerDown={e => e.stopPropagation()}
                                    >
                                        Usuń datę
                                    </DeleteButton>}
                                </DateBox>
                            )}
                        </Paper>
                    </>
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        pt: 2,
                    }}
                >
                    <CustomInput
                        label="Link"
                        value={imageLink}
                        setValue={setImageLink}
                        mode="text"
                        variant='filled'
                        sx={{
                            width: '97%',
                            background: `${isLinkChanged ? '#ccff90' : 'transparent'}`,
                        }}
                    />
                </Box>
            </Box>
        </ListItem>
    )
}

export default React.memo(SliderItem);
